import { useQuery } from '@tanstack/react-query';

import { CHAIN_IDS } from '@/constants/chain';

import { fetchJsonOrThrow } from '@/utils/fetch';
import { getPortfolioRefreshNonce } from '@/utils/portfolioRefresh';
import { retryNetworkOrServerError } from '@/utils/queryClient';
import { routes } from '@/utils/routes';
import type { WalletPnL } from '@/utils/zerion/types';

interface UseWalletPnLParams {
  evmAddress?: string;
  solanaAddress?: string;
  enabled?: boolean;
  /** Optional chain filter - if provided, only fetch PnL for this chain */
  chainId?: number | 'all';
}

const fetchPnL = (url: string) => fetchJsonOrThrow<WalletPnL>(url);

function buildUrl(
  { evmAddress, solanaAddress, chainId }: UseWalletPnLParams,
  refreshNonce: string,
): string | null {
  // Skip an address if the chain filter excludes its chain type.
  const includeEvm = !chainId || chainId === 'all' || chainId !== CHAIN_IDS.SOLANA;
  const includeSolana = !chainId || chainId === 'all' || chainId === CHAIN_IDS.SOLANA;

  const effectiveEvm = includeEvm ? evmAddress : undefined;
  const effectiveSolana = includeSolana ? solanaAddress : undefined;
  if (!effectiveEvm && !effectiveSolana) return null;

  const params = new URLSearchParams();
  if (effectiveEvm) params.set('evmAddress', effectiveEvm);
  if (effectiveSolana) params.set('solanaAddress', effectiveSolana);
  if (chainId && chainId !== 'all') params.set('chains', String(chainId));
  if (refreshNonce) params.set('_refresh', refreshNonce);
  return `${routes.api.WALLET_PNL}?${params.toString()}`;
}

export const walletPnLQueryKey = (params: UseWalletPnLParams) => ['wallet-pnl', params] as const;

export function useWalletPnL({
  evmAddress,
  solanaAddress,
  enabled = true,
  chainId,
}: UseWalletPnLParams) {
  const params = { evmAddress, solanaAddress, chainId };

  const { data, ...rest } = useQuery<WalletPnL, Error>({
    queryKey: walletPnLQueryKey(params),
    queryFn: () => fetchPnL(buildUrl(params, getPortfolioRefreshNonce())!),
    enabled: enabled && buildUrl(params, '') !== null,
    staleTime: 60_000,
    retry: retryNetworkOrServerError,
  });

  return {
    pnl: data ?? null,
    ...rest,
  };
}
